<template>
  <div class="tab-content" @click="closeDropdown">
    <div class="app-download-section">
      <div class="phone-image d-none d-md-block">
        <img :src="phoneImage" alt="HiiKER app on phone" />
      </div>

      <div class="qr-section">

        <div :class="isMobileDevice ? 'd-flex flex-column align-items-center' : ''">
          <img
            class="img-fluid mb-3 promo-image"
            :src="promoImage"
            alt="Award winning hiking app"
            style="opacity: 0.65;"
          />
        </div>

        <div v-if="isUserLoggedIn && isMobileDevice" class="p-3 mb-2 d-flex flex-column align-items-center text-center">
          <h3 class="display-5 mb-3">View this {{ resourceLabel }} in the <strong>HiiKER app</strong></h3>
          <a class="btn btn-primary btn-lg" :href="qrCodeUrl" @click="handleOpenInApp">Open in app</a>
        </div>

        <div v-else-if="isUserLoggedIn && !isMobileDevice" class="qr-container card p-2 mb-2">
          <div class="qr-code">
            <img :src="`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrCodeUrl)}&size=400x400`" :alt="`QR code for the ${resourceLabel} ${resourceTitle}`" />
          </div>

          <h3 class="display-5">Scan the <strong>QR code</strong> to view on the HiiKER app.</h3>
        </div>

        <div v-else class="login-content mb-1">
          <h3 class="display-5"><strong>Get this {{ resourceLabel }} on your phone in two seconds.</strong></h3>
          <p><small>Sign in and we will sync it to your device.</small></p>
          <button class="btn btn-primary" @click="handleLogin">Sign in and sync</button>
          <hr class="my-3">
        </div>

        <template v-if="!isMobileDevice">

          <div class="qr-text">
            <p class="mb-1"><small>Or get a link sent directly via text message.</small></p>
          </div>

          <div class="phone-number-input input-group mb-2">
            <div class="dropdown" :class="{ show: isDropdownOpen }">
              <button class="btn btn-outline-secondary dropdown-toggle country-code-dropdown" type="button" id="countryCodeDropdown" @click.stop="toggleDropdown" aria-expanded="false">
                {{ selectedCountryCode }}
              </button>
              <ul class="dropdown-menu dropdown-menu-dark" :class="{ show: isDropdownOpen }" aria-labelledby="countryCodeDropdown">
                <li v-for="country in countryCodes" :key="country.code">
                  <a class="dropdown-item" href="#" @click.prevent="selectCountry(country.code)">{{ country.name }} ({{ country.code }})</a>
                </li>
              </ul>
            </div>
            <input type="tel" class="form-control" placeholder="Your phone number" v-model="phoneNumber" aria-label="Phone number input">
            <button class="btn btn-primary" type="button" @click="sendSmsLink">Send</button>
          </div>

          <small class="text-muted">By entering your number and clicking send, you express consent to receive SMS messages from HiiKER</small>

          <div class="store-buttons" style="display: none;">
            <a href="https://play.google.com/store/apps/details?id=com.hiiker" target="_blank" rel="noopener noreferrer">
              <img :src="googlePlayBadge" alt="Get it on Google Play" />
            </a>
            <a href="https://apps.apple.com/app/hiiker/id1470447885" target="_blank" rel="noopener noreferrer">
              <img :src="appStoreBadge" alt="Download on the App Store" />
            </a>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import phoneImage from '../../images/download-modal-1.webp';
import promoImage from '../../images/download-promo.webp';
import googlePlayBadge from '../../images/google-play-btn-white.png';
import appStoreBadge from '../../images/app-app-store-btn-white.png';
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";
import countryCodesData from '../../utils/json/CountryCodes.json';
import { getDownloadResourceContext, getResourceNoun } from './context';

// Type declarations
declare global {
  interface Window {
    mixpanel?: {
      track: (event: string, properties?: any) => void;
    };
  }
}

// Props
const props = defineProps<{
  userData: any;
}>();

// Emits
const emit = defineEmits<{
  close: [];
}>();

// Reactive properties for phone number input
const selectedCountryCode = ref('+44'); // Default to UK
const phoneNumber = ref('');
const isDropdownOpen = ref(false);

// Mobile detection
const isMobileDevice = ref(false);

// Use country codes from the imported JSON file
const countryCodes = countryCodesData.map(country => ({
  name: country.name,
  code: country.dial_code
}));

// Computed
const isUserLoggedIn = computed(() => props.userData != null);

const resourceContext = computed(() => getDownloadResourceContext());
const resourceNoun = computed(() => getResourceNoun(resourceContext.value.graphqlType));
const resourceLabel = computed(() => resourceNoun.value);
const resourceLabelTitleCase = computed(() =>
  resourceLabel.value.charAt(0).toUpperCase() + resourceLabel.value.slice(1)
);
const resourceTitle = computed(() =>
  resourceContext.value.name || `this ${resourceLabel.value}`
);
const qrCodeUrl = computed(() => resourceContext.value.shareUrl || window.location.href);

const handleLogin = () => {
  if (window.onBoardingVue && window.onBoardingVue.launchFor) {
    window.onBoardingVue.launchFor(OnBoardingGoal.DownloadApp);
  }
};

const sendSmsLink = async () => {
  const fullPhoneNumber = selectedCountryCode.value + phoneNumber.value;
  const context = resourceContext.value;
  const resourceId = context.id;

  if (!resourceId) {
    alert(`${resourceLabelTitleCase.value} information not available`);
    return;
  }

  // Basic phone number validation
  if (!phoneNumber.value || phoneNumber.value.length < 5) {
    alert('Please enter a valid phone number');
    return;
  }

  try {
    const response = await fetch('/api/v1/sms/send-trail-link', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({
        phone_number: fullPhoneNumber,
        resource_type: context.graphqlType,
        resource_id: resourceId,
        trail_id: context.graphqlType === 'Trail' ? resourceId : undefined,
        recording_id: context.graphqlType === 'Recording' ? resourceId : undefined
      })
    });

    const data = await response.json();

    const analyticsPayload: Record<string, unknown> = {
      phone_number: fullPhoneNumber,
      resource_type: context.graphqlType,
      resource_id: resourceId
    };

    if (context.graphqlType === 'Trail') {
      analyticsPayload.trail_id = resourceId;
    } else if (context.graphqlType === 'Recording') {
      analyticsPayload.recording_id = resourceId;
    }

    window.mixpanel?.track('SMS Trail Link Sent', analyticsPayload);

    if (response.status === 429) {
      // Rate limit exceeded
      const retryMinutes = Math.ceil((data.retry_after || 3600) / 60);
      alert(`Rate limit exceeded. Please try again in ${retryMinutes} minutes.`);
    } else if (data.success) {
      alert(`${resourceLabelTitleCase.value} link sent successfully!`);
      phoneNumber.value = ''; // Clear input after sending
    } else {
      alert(`Failed to send SMS: ${data.error || 'Unknown error'}`);
    }
  } catch (error) {
    console.error('SMS sending error:', error);
    alert('Failed to send SMS. Please try again.');
  }
};

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = () => {
  isDropdownOpen.value = false;
};

const selectCountry = (code: string) => {
  selectedCountryCode.value = code;
  isDropdownOpen.value = false;
};

const handleOpenInApp = () => {
  // Track Mixpanel event when "Open in app" is clicked
  if (window.mixpanel) {
    const context = resourceContext.value;
    const analyticsPayload: Record<string, unknown> = {
      resource_type: context.graphqlType,
      resource_id: context.id,
      resource_name: resourceTitle.value,
      device_type: 'mobile',
      user_logged_in: true
    };

    if (context.graphqlType === 'Trail') {
      analyticsPayload.trail_id = context.id;
      analyticsPayload.trail_name = resourceTitle.value;
    } else if (context.graphqlType === 'Recording') {
      analyticsPayload.recording_id = context.id;
      analyticsPayload.recording_name = resourceTitle.value;
    }

    window.mixpanel.track('Open in App Clicked', analyticsPayload);
  }
};

// Initialize mobile detection
onMounted(() => {
  // Check if the device is mobile based on user agent and screen size
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/;
  const isMobileUA = mobileKeywords.test(userAgent);
  const isMobileScreen = window.innerWidth <= 768;

  isMobileDevice.value = isMobileUA || isMobileScreen;
});

</script>

<style scoped lang="scss">
// ... (your existing styles) ...

.phone-number-input {
  display: flex; /* Use flexbox to align items */
  width: 100%; /* Ensure it takes full width */
  margin-top: 10px; /* Add some space above */

  .dropdown-toggle {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    white-space: nowrap; // Prevent text wrapping
    overflow: hidden; // Hide overflow
    text-overflow: ellipsis; // Show ellipsis for truncated text
    max-width: 100px; // Adjust as needed
  }

  .form-control {
    border-radius: 0; /* Remove rounded corners for input */
    flex-grow: 1; /* Allow the input to take available space */
  }

  .btn-primary {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    background-color: #f9e547; /* HiiKER app yellow */
    border-color: #f9e547;
    color: #1a1a1a;
    font-weight: 600;

    &:hover {
      background-color: darken(#f9e547, 10%);
      border-color: darken(#f9e547, 10%);
    }
  }
}

.dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  display: none;
  min-width: 10rem;
  padding: 0.5rem 0;
  margin: 0.125rem 0 0;
  font-size: 1rem;
  color: #212529;
  text-align: left;
  list-style: none;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;

  &.show {
    display: block;
  }
}

.dropdown-menu-dark {
  background-color: #343a40; /* Dark background for dropdown */
  border-color: #495057;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.25rem 1.5rem;
  clear: both;
  font-weight: 400;
  color: #212529;
  text-align: inherit;
  text-decoration: none;
  white-space: nowrap;
  background-color: transparent;
  border: 0;

  &:hover, &:focus {
    color: #1e2125;
    background-color: #e9ecef;
  }
}

.dropdown-menu-dark .dropdown-item {
  color: #f8f9fa; /* Light text color */

  &:hover {
    background-color: #495057; /* Slightly lighter hover background */
    color: #fff;
  }
}

.tab-content {
  animation: fadeIn 0.3s ease-in-out;
  padding: 2em;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.app-download-section {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.phone-image {
  flex: 0 0 300px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.promo-image{
  max-width: 300px;
}

.qr-section {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.qr-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.5em;
  width: 100%;
}

.qr-code {
  width: 100%; /* Make the QR code container take full width */
  max-width: 135px; /* Keep a maximum width to prevent it from becoming too large */
  height: auto; /* Allow height to adjust proportionally */
  background-color: white;
  padding: 3px;
  border: 3px solid #fddf00;
  border-radius: 8px;
  display: flex;
  justify-content: center; /* Center the QR image horizontally within its container */
  align-items: center; /* Center the QR image vertically within its container */

  img {
    width: 100%; /* Make the QR image fill its container */
    height: auto; /* Maintain aspect ratio */
    display: block; /* Remove any extra space below the image */
  }
}

.store-buttons {
  display: flex;
  gap: 12px;
  margin-top: 8px;

  a {
    display: inline-block;
    height: 45px;

    img {
      height: 100%;
      width: auto;
    }
  }
}

.login-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  h3 {
    font-size: 24px;
    margin-bottom: 6px;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 24px;
  }

  .btn-primary {
    background-color: #f9e547;
    border-color: #f9e547;
    color: #1a1a1a;
    font-weight: 600;
    padding: 12px 32px;
    align-self: flex-start;

    &:hover {
      background-color: darken(#f9e547, 10%);
      border-color: darken(#f9e547, 10%);
    }
  }
}

.mobile-app-container {
  text-align: center;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);

  h3 {
    font-size: 20px;
  }

  .btn-primary {
    background-color: #f9e547;
    border-color: #f9e547;
    color: #1a1a1a;
    font-weight: 600;
    padding: 14px 36px;
    font-size: 18px;

    &:hover {
      background-color: darken(#f9e547, 10%);
      border-color: darken(#f9e547, 10%);
    }
  }
}

@media (max-width: 768px) {
  .phone-image {
    flex: 1;
    max-width: 100%;
  }
}
</style>
